/**
 * LightComponent - abstract base for all Genesys light wrappers.
 * Encapsulates THREE.Light with color, intensity and shadow configuration.
 */

import * as THREE from 'three';

import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { Actor } from '../../actors/Actor.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface LightComponentOptions extends SceneComponentOptions {
  /** Color of the light */
  color?: THREE.ColorRepresentation;
  /** Intensity of the light */
  intensity?: number;
  /** Whether the light casts shadows */
  castShadow?: boolean;
  /** Shadow map size */
  shadowMapSize?: number;
  /** Shadow bias */
  shadowBias?: number;
  /** Shadow normal bias - helps reduce shadow acne */
  shadowNormalBias?: number;
  /** Shadow near plane */
  shadowNear?: number;
  /** Shadow far plane - used by each shadow camera (for CSM this is separate from csmMaxFar view coverage, should be larger than csmMaxFar) */
  shadowFar?: number;
  /** Shadow radius - controls softness of shadow edges (default: 1) */
  shadowRadius?: number;
}

/** Light type that may have shadow (DirectionalLight, SpotLight, PointLight). AmbientLight has no shadow. */
type LightWithShadow = THREE.Light & { shadow?: THREE.LightShadow };

type LightWithTarget = THREE.Light & { target?: THREE.Object3D };

/** Abstract base class for all light components. Wraps a THREE.Light. */
export abstract class LightComponent<TLight extends THREE.Light, TLightOptions extends LightComponentOptions> extends SceneComponent {
  private static tempWorldPosition = new THREE.Vector3();
  private static tempWorldDirection = new THREE.Vector3();

  // Backing fields
  private _shadowMapSize: number = 2048;
  private _shadowBias: number = 0;
  private _shadowNormalBias: number = 0;
  private _shadowNear: number = 0.1;
  private _shadowFar: number = 100;
  private _shadowRadius: number = 3;
  private _light: TLight | null = null;

  /** The THREE.Light object managed by this component */
  @property({ hidden: true })
  protected get light(): TLight | null {
    return this._light;
  }
  protected set light(value: TLight | null) {
    if (this._light === value) return;
    if (this._light) {
      this._light.removeFromParent();
      this._light.dispose();
    }
    this._light = value;
    if (value) {
      this.add(value);
      this.bindObject3DProperties(value, ['castShadow', 'receiveShadow']);
      // clear out the transform of the light since some of them may have builtin offset
      // like the spot light...
      // NOTE: HemisphereLight uses its position as the light direction (sky direction).
      // Resetting it to (0,0,0) creates a zero-length direction vector, causing black rendering.
      if (!(value instanceof THREE.HemisphereLight)) {
        value.position.set(0, 0, 0);
        value.rotation.set(0, 0, 0);
        value.scale.set(1, 1, 1);
      }
    }
    this.recreateLightHelper();
    this.recreateCameraHelper();
  }

  protected lightHelper: THREE.Object3D | null = null;
  protected cameraHelper: THREE.CameraHelper | null = null;

  @property({ transient: true })
  get color(): THREE.Color {
    return this.light?.color ?? new THREE.Color(1, 1, 1);
  }
  set color(value: THREE.ColorRepresentation) {
    this.light?.color.set(value);
  }

  @property({ transient: true })
  get intensity(): number {
    return this.light?.intensity ?? 1;
  }
  set intensity(value: number) {
    if (this.light) {
      this.light.intensity = value;
    }
  }

  @property({ description: 'Higher resolution for better quality' })
  get shadowMapSize(): number {
    return this._shadowMapSize;
  }
  set shadowMapSize(value: number) {
    this._shadowMapSize = value;
    this.configureShadow({ mapSize: value });
  }

  @property({ description: 'Conservative negative bias (will be auto-adjusted)' })
  get shadowBias(): number {
    return this._shadowBias;
  }
  set shadowBias(value: number) {
    this._shadowBias = value;
    this.configureShadow({ bias: value });
  }

  @property({ description: 'Conservative normal bias (will be auto-adjusted)' })
  get shadowNormalBias(): number {
    return this._shadowNormalBias;
  }
  set shadowNormalBias(value: number) {
    this._shadowNormalBias = value;
    this.configureShadow({ normalBias: value });
  }

  @property({ description: 'Near plane used by each shadow camera (for CSM this applies per cascade)' })
  get shadowNear(): number {
    return this._shadowNear;
  }
  set shadowNear(value: number) {
    this._shadowNear = value;
    this.configureShadow({ near: value });
  }

  @property({ description: 'Far plane used by each shadow camera (for CSM this is separate from csmMaxFar view coverage, should be larger than csmMaxFar)' })
  get shadowFar(): number {
    return this._shadowFar;
  }
  set shadowFar(value: number) {
    this._shadowFar = value;
    this.configureShadow({ far: value });
  }

  @property({ description: 'Shadow softness radius for PCFShadowMap' })
  get shadowRadius(): number {
    return this._shadowRadius;
  }
  set shadowRadius(value: number) {
    this._shadowRadius = value;
    this.configureShadow({ radius: value });
  }

  protected abstract createLight(): TLight;

  public override initialize(options?: LightComponentOptions): void {
    super.initialize(options);

    this.light = this.createLight();

    this.color = options?.color ?? this.color;
    this.intensity = options?.intensity ?? this.intensity;
    this.shadowMapSize = options?.shadowMapSize ?? this.shadowMapSize;
    this.shadowBias = options?.shadowBias ?? this.shadowBias;
    this.shadowNormalBias = options?.shadowNormalBias ?? this.shadowNormalBias;
    this.shadowNear = options?.shadowNear ?? this.shadowNear;
    this.shadowFar = options?.shadowFar ?? this.shadowFar;
    this.shadowRadius = options?.shadowRadius ?? this.shadowRadius;
    this.castShadow = options?.castShadow ?? this.castShadow;

    this.configureInitialLightProperties();
  }

  public override postLoad(): void {
    super.postLoad();
    if (!this.light) {
      // re-establish the light reference in case it was not serialized as a property
      this.light = this.getComponent(THREE.Light) as TLight;
      if (!this.light) {
        // Fallback: create a new light if none was found
        this.light = this.createLight();
      }
    }

    this.configureInitialLightProperties();
  }

  public override endPlay(): void {
    // this detaches and disposes the light
    this.light = null;
    super.endPlay();
  }

  protected configureInitialLightProperties() {
    this.configureShadow({
      mapSize: this.shadowMapSize,
      bias: this.shadowBias,
      normalBias: this.shadowNormalBias,
      near: this.shadowNear,
      far: this.shadowFar,
      radius: this.shadowRadius,
    });

  }

  protected configureShadow(options: { mapSize?: number; bias?: number; normalBias?: number; near?: number; far?: number; radius?: number } = {}): this {
    const lightWithShadow = this.light as LightWithShadow;
    if (!lightWithShadow.shadow) return this;

    const { mapSize, bias, normalBias, near, far, radius } = options;

    const shadow = lightWithShadow.shadow;

    if (mapSize !== undefined && (shadow.mapSize.width !== mapSize || shadow.mapSize.height !== mapSize)) {
      shadow.mapSize.width = mapSize;
      shadow.mapSize.height = mapSize;
      // dispose the old shadow so that the size change takes effect
      shadow.dispose();
    }

    if (bias !== undefined) {
      shadow.bias = bias;
    }

    if (normalBias !== undefined) {
      shadow.normalBias = normalBias;
    }

    if (radius !== undefined) {
      shadow.radius = radius;
    }

    const camera = shadow.camera;

    if (camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera) {
      if (near !== undefined) camera.near = near;
      if (far !== undefined) camera.far = far;
      camera.updateProjectionMatrix();
    }

    return this;
  }

  public showHelper(show: boolean): this {
    if (show === !!this.lightHelper) return this;
    if (show) {
      this.lightHelper = this.createLightHelper();
    } else {
      this.disposeLightHelper();
    }
    return this;
  }

  protected abstract createLightHelper(): THREE.Object3D | null;

  /**
   * Detaches and disposes the light helper. Three.js light helpers
   * (`DirectionalLightHelper`, `PointLightHelper`, `SpotLightHelper`,
   * `HemisphereLightHelper`) own per-instance geometries/materials whose
   * `dispose()` releases the renderer's cached `RenderObject`s and the
   * `dispose` listeners they registered on those resources. Skipping this
   * leaks one helper subtree per actor on every editor tear-down cycle.
   */
  protected disposeLightHelper(): void {
    if (!this.lightHelper) return;
    this.lightHelper.removeFromParent();
    (this.lightHelper as { dispose?: () => void }).dispose?.();
    this.lightHelper = null;
  }

  public showCameraHelper(show: boolean): this {
    if (show === !!this.cameraHelper) return this;
    const lightWithShadow = this.light as LightWithShadow;
    if (show && lightWithShadow.shadow) {
      this.cameraHelper = new THREE.CameraHelper(lightWithShadow.shadow.camera);
    } else {
      this.disposeCameraHelper();
    }
    return this;
  }

  protected disposeCameraHelper(): void {
    if (!this.cameraHelper) return;
    this.cameraHelper.removeFromParent();
    this.cameraHelper.dispose();
    this.cameraHelper = null;
  }

  public isHelperVisible(): boolean {
    return !!this.lightHelper;
  }

  public isCameraHelperVisible(): boolean {
    return !!this.cameraHelper;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.updateHelpers();
  }

  protected updateHelpers(): void {
    // Update helper transform if helper is visible
    if (this.lightHelper) {
      this.lightHelper.visible = this.visible;
      if (this.lightHelper.parent === null) {
        // add the helper to the scene directly so it's not affected by the light component's transform
        this.getWorld()?.scene.add(this.lightHelper);
      }
      this.lightHelper.updateMatrixWorld();
      if (typeof (this.lightHelper as any).update === 'function') {
        (this.lightHelper as any).update();
      }
    }

    // Update camera helper if it exists
    if (this.cameraHelper) {
      this.cameraHelper.visible = this.visible && this.castShadow;
      if (this.cameraHelper.parent === null) {
        // add the helper to the scene directly so it's not affected by the light component's transform
        this.getWorld()?.scene.add(this.cameraHelper);
      }
      this.cameraHelper.update();
    }
  }


  protected recreateLightHelper(): void {
    if (this.lightHelper) {
      this.showHelper(false);
      this.showHelper(true);
    }
  }

  protected recreateCameraHelper(): void {
    if (this.cameraHelper) {
      this.showCameraHelper(false);
      this.showCameraHelper(true);
    }
  }

  public override updateMatrixWorld(force?: boolean): void {
    super.updateMatrixWorld(force);
    if (!this.light) {
      return;
    }
    const target = (this.light as LightWithTarget).target;
    if (!target) {
      return;
    }

    // maually adjust the light target position so that the light direction follows this component
    const worldPosition = LightComponent.tempWorldPosition;
    const worldDirection = LightComponent.tempWorldDirection;
    this.getWorldPosition(worldPosition);
    this.getWorldDirection(worldDirection);
    // the 0.5 is a somewhat nasty hack, it ensures the actual shadow camera overshoots the light target (which is likely to follow the pawn for the directional light)
    const shadowDistance = this.shadowFar * 0.5;
    // NOTE: the "forward" direction is not the same between the light compoennt and the light object due to the "isLight" flag
    // here we negate the direction on the light component so that it is possible to do the following:
    /*
    * const light = DirectionalLightComponent.create(...);
    * light.position = xxx;
    * light.lookAt(yyy);
    */
    // without the negation, the light position and target would need to be swapped which is really fucking weird...
    target.setWorldPosition(worldPosition.sub(worldDirection.multiplyScalar(-shadowDistance)));
    // manually update the target's matrix world since it's not attached
    target.updateMatrixWorld();
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    this.updateHelpers();
  }

  public override onEditorSelectedChanged(isSelected: boolean, selectedActors: Actor[]): void {
    super.onEditorSelectedChanged(isSelected, selectedActors);
    this.showHelper(isSelected);
    this.showCameraHelper(isSelected);
    this.updateHelpers();
  }

  public override onEditorRemoveFromWorld(): void {
    // Helpers are added directly to `world.scene` (not as children of this
    // component), so removing the actor's root component from its parent
    // does not detach them. Without this hook they would stay in the scene
    // graph and keep their renderer-side resources alive forever.
    this.disposeLightHelper();
    this.disposeCameraHelper();
    super.onEditorRemoveFromWorld();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Light';
  }
}
